import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { site, trips, heroImages } from "../data/mock";
import { PageHero, Eyebrow } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import api from "../lib/api";
import { Phone, Mail, MapPin, MessageCircle, Send, CheckCircle2 } from "lucide-react";

const Contact = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", trip: trips[0].name, message: "" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    // Save the lead in the backend for the admin dashboard...
    api.createLead({
      name: form.name, email: form.email, phone: form.phone,
      trip: form.trip, message: form.message, source: "contact",
    }).catch(() => {});
    // ...and open WhatsApp to message the owners directly.
    const text =
      `Hi Hog Tide! I'd like to book a trip.%0A` +
      `Name: ${form.name}%0A` +
      `Phone: ${form.phone}%0A` +
      `Email: ${form.email}%0A` +
      `Interested in: ${form.trip}` +
      (form.message ? `%0AMessage: ${form.message}` : "");
    window.open(`https://wa.me/${site.whatsapp}?text=${text}`, "_blank", "noopener");
    setSent(true);
    toast({ title: t("contactPage.sentTitle"), description: t("contactPage.sentDesc") });
  };

  return (
    <>
      <PageHero image={heroImages[3].img} eyebrow={t("contactPage.heroEyebrow")} title={t("contactPage.heroTitle")} sub={t("contactPage.heroSub")} />
      <section className="py-14">
        <div className="container-x grid lg:grid-cols-3 gap-10">
          <div className="space-y-4">
            <Eyebrow>{t("contactPage.getInTouch")}</Eyebrow>
            <a href={`tel:${site.phoneRaw}`} className="flex items-center gap-3 rounded-xl bg-[#0e2740] border border-white/10 p-4 hover:border-[#f5a524]/40 transition"><span className="rounded-lg bg-[#f5a524]/15 p-2.5 text-[#f5a524]"><Phone className="h-5 w-5" /></span><div><p className="text-xs text-slate-400">{t("contactPage.callUs")}</p><p className="text-white font-heading">{site.phone}</p></div></a>
            <a href={`https://wa.me/${site.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl bg-[#0e2740] border border-white/10 p-4 hover:border-[#25D366]/40 transition"><span className="rounded-lg bg-[#25D366]/15 p-2.5 text-[#25D366]"><MessageCircle className="h-5 w-5" /></span><div><p className="text-xs text-slate-400">{t("contactPage.whatsapp")}</p><p className="text-white font-heading">{site.phone}</p></div></a>
            <a href={`mailto:${site.email}`} className="flex items-center gap-3 rounded-xl bg-[#0e2740] border border-white/10 p-4 hover:border-[#f5a524]/40 transition"><span className="rounded-lg bg-[#f5a524]/15 p-2.5 text-[#f5a524]"><Mail className="h-5 w-5" /></span><div><p className="text-xs text-slate-400">{t("contactPage.email")}</p><p className="text-white font-heading break-all">{site.email}</p></div></a>
            <div className="flex items-center gap-3 rounded-xl bg-[#0e2740] border border-white/10 p-4"><span className="rounded-lg bg-[#f5a524]/15 p-2.5 text-[#f5a524]"><MapPin className="h-5 w-5" /></span><div><p className="text-xs text-slate-400">{t("contactPage.location")}</p><p className="text-white font-heading">{site.location}</p></div></div>
          </div>

          <div className="lg:col-span-2">
            <div className="rounded-2xl border border-white/10 bg-[#0e2740] p-6 md:p-8">
              {sent ? (
                <div className="text-center py-12"><CheckCircle2 className="h-16 w-16 text-emerald-400 mx-auto mb-4" /><h3 className="font-heading text-2xl text-white">{t("contactPage.thanks", { name: form.name.split(" ")[0] })}</h3><p className="text-slate-300 mt-2">{t("contactPage.thanksMsg")}</p><a href={`https://wa.me/${site.whatsapp}`} target="_blank" rel="noreferrer"><Button className="btn-gold mt-5">{t("contactPage.chatWhatsApp")}</Button></a></div>
              ) : (
                <form onSubmit={submit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="text-slate-300">{t("contactPage.fullName")}</Label><Input required value={form.name} onChange={(e)=>set("name",e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-slate-500" placeholder={t("contactPage.namePlaceholder")} /></div>
                    <div><Label className="text-slate-300">{t("contactPage.phone")}</Label><Input required value={form.phone} onChange={(e)=>set("phone",e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-slate-500" placeholder="+1 555 000 1234" /></div>
                  </div>
                  <div><Label className="text-slate-300">{t("contactPage.email")}</Label><Input required type="email" value={form.email} onChange={(e)=>set("email",e.target.value)} className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-slate-500" placeholder="you@email.com" /></div>
                  <div><Label className="text-slate-300">{t("contactPage.interestedIn")}</Label>
                    <select value={form.trip} onChange={(e)=>set("trip",e.target.value)} className="mt-1 w-full h-10 rounded-md bg-white/5 border border-white/15 px-3 text-sm text-white focus:outline-none focus:border-[#f5a524]">
                      {trips.map((t2)=>(<option key={t2.slug} className="text-black">{t2.name}</option>))}
                      <option className="text-black">{t("contactPage.notSureYet")}</option>
                    </select>
                  </div>
                  <div><Label className="text-slate-300">{t("contactPage.message")}</Label><Textarea value={form.message} onChange={(e)=>set("message",e.target.value)} rows={4} className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-slate-500" placeholder={t("contactPage.messagePlaceholder")} /></div>
                  <Button type="submit" className="btn-gold w-full h-12 gap-2"><Send className="h-4 w-4" /> {t("contactPage.sendMessage")}</Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;
